import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Field from '../../../../../components/Form/Field'
import SpreadSheet from '../../../../../components/SpreadSheet'
import FieldLabelWrapper from '../../../../../features/components/Form/FieldLabelWrapper'

import { formatOptionsList } from '../../../../../features/formatters'

import { NAME, LOCATION_LIST_TYPE, COUNTRY, ADDRESSES, VALIDATION_ERROR_CODE } from '../fields'
import locationListTypes from '../../../../../constants/selectLists/locationListTypes'

import { clearValidateLocationAddresses } from '../../../../../modules/actions/location'
import { locationAddressesValidationErrorSelector } from '../../../../../modules/selectors/location'
import { choicesCountriesSelector } from '../../../../../modules/selectors/choices'

import useStyles from '../styles'
import useDrawerFormStyles from '../../../../../styles/common/drawerForms'

const spreadSheetColumnLabels = ['Name', 'Address and/or Postcode']
const LocationListFormFields = ({ formik, addressesErrorIndexes, isEditForm }) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const drawerFormClasses = useDrawerFormStyles()

  const { t } = useTranslation()

  const countries = useSelector(choicesCountriesSelector)
  const locationAddressesValidationError = useSelector(locationAddressesValidationErrorSelector)

  const isLocationAddressesValidationError =
    locationAddressesValidationError && locationAddressesValidationError.code === VALIDATION_ERROR_CODE

  const { values, touched, errors, setFieldValue } = formik

  const formattedCountries = formatOptionsList({
    list: countries,
    labelName: 'name',
    valueName: 'id'
  })

  useEffect(() => {
    // clear locationAddressesValidationError on form close
    return () => {
      dispatch(clearValidateLocationAddresses())
    }
  }, [dispatch])

  return (
    <>
      <section className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>{t('Location list settings')}</h3>
        <FieldLabelWrapper label={t('Name')} labelFor={NAME}>
          <Field
            placeholder={t('Name of location list')}
            formik={formik}
            name={NAME}
            enableReinitialize={!!isEditForm}
          />
        </FieldLabelWrapper>
        <FieldLabelWrapper label={t('List Type')}>
          <Field
            options={locationListTypes}
            formik={formik}
            id={LOCATION_LIST_TYPE}
            name={LOCATION_LIST_TYPE}
            placeholder={t('List type')}
          />
        </FieldLabelWrapper>
        <FieldLabelWrapper label={t('Country')}>
          <Field options={formattedCountries} formik={formik} id={COUNTRY} name={COUNTRY} placeholder={t('Country')} />
        </FieldLabelWrapper>
      </section>
      <section className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>{t('List of locations (up to 500)')}</h3>
        <p>
          {t(
            'You can paste the list from another spreadsheet and we will automatically add enough rows. You can give the location a name (e.g. Decathlon Cambridge) and provide either the full address or just the postcode. We will then match the addresses to map locations. The maximum number of locations you can target in an ad set is 500, so we also limit our lists to 500. Please don’t paste a list any longer than that.'
          )}
        </p>
        <SpreadSheet
          name={ADDRESSES}
          setFieldValue={setFieldValue}
          formikData={values[ADDRESSES]}
          columnLabels={spreadSheetColumnLabels}
          maxRows={500}
          className={classes.spreadSheet}
          tableClassName={classes.spreadSheetTable}
          error={
            (touched[ADDRESSES] && errors[ADDRESSES]) ||
            (isLocationAddressesValidationError
              ? t(
                  'Sorry, one or more of the addresses you have provided could not be matched. Please update the address (highlighted with red) to correct any errors or remove it'
                )
              : null)
          }
          errorRowsIndexes={addressesErrorIndexes}
        />
      </section>
    </>
  )
}

LocationListFormFields.propTypes = {
  formik: PropTypes.object,
  addressesErrorIndexes: PropTypes.array,
  isEditForm: PropTypes.bool
}

export default LocationListFormFields
